export interface PageableLike {
  pageNumber?: number;
  pageSize?: number;
}

export interface PagedResponseLike<TItem> {
  content?: TItem[];
  pageable?: PageableLike;
  number?: number;
  last?: boolean;
}

export interface FetchAllPagedContentOptions<TItem, TResponse extends PagedResponseLike<TItem>> {
  fetchPage: (pageNumber: number, pageSize?: number) => Promise<TResponse>;
  initialPageNumber?: number;
  initialPageSize?: number;
  maxPages?: number;
}

const DEFAULT_MAX_PAGES = 100;

function toNonNegativeInt(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) return undefined;
  return value;
}

function resolveResponsePageNumber<TItem>(
  response: PagedResponseLike<TItem>,
  requestedPageNumber: number,
): number {
  return (
    toNonNegativeInt(response.number) ??
    toNonNegativeInt(response.pageable?.pageNumber) ??
    requestedPageNumber
  );
}

export async function fetchAllPagedContent<TItem, TResponse extends PagedResponseLike<TItem>>({
  fetchPage,
  initialPageNumber = 0,
  initialPageSize,
  maxPages = DEFAULT_MAX_PAGES,
}: FetchAllPagedContentOptions<TItem, TResponse>): Promise<TItem[]> {
  const allItems: TItem[] = [];
  const seenPageNumbers = new Set<number>();

  let pageNumber = initialPageNumber;
  let pageSize = initialPageSize;

  for (let i = 0; i < maxPages; i++) {
    const response = await fetchPage(pageNumber, pageSize);
    const responsePageNumber = resolveResponsePageNumber(response, pageNumber);

    // Protection against backends repeating the same page endlessly.
    if (seenPageNumbers.has(responsePageNumber)) {
      break;
    }
    seenPageNumbers.add(responsePageNumber);

    if (pageSize === undefined) {
      const responsePageSize = toNonNegativeInt(response.pageable?.pageSize);
      if (responsePageSize && responsePageSize > 0) {
        pageSize = responsePageSize;
      }
    }

    const content = Array.isArray(response.content) ? response.content : [];
    allItems.push(...content);

    if (response.last === true) {
      break;
    }

    pageNumber = responsePageNumber + 1;
  }

  return allItems;
}
